import { useEffect, useMemo, useState } from 'react';
import { exchangeCenterViewCodeAPI } from '../../api/superAdminAuth';
import { clearCenterViewSession, markCenterViewTab, setCenterViewSession } from '../../lib/utils';

const exchangeRequests = new Map<
    string,
    ReturnType<typeof exchangeCenterViewCodeAPI>
>();

const exchangeCenterViewCodeOnce = (code: string) => {
    const existingRequest = exchangeRequests.get(code);
    if (existingRequest) return existingRequest;

    const request = exchangeCenterViewCodeAPI(code);
    exchangeRequests.set(code, request);
    request.catch(() => exchangeRequests.delete(code));
    return request;
};

const normalizeExpiresAt = (value: unknown) => {
    if (typeof value === 'number' && value < 1_000_000_000_000) return value * 1000;
    return value as string | number | Date | null | undefined;
};

const CenterViewBootstrap = () => {
    const code = useMemo(() => new URLSearchParams(window.location.search).get('code'), []);
    const [error, setError] = useState('');

    useEffect(() => {
        markCenterViewTab();
        if (!code) {
            setError('유효한 센터 열람 코드가 없습니다.');
            return;
        }

        let cancelled = false;
        const exchange = async () => {
            try {
                // React StrictMode mounts effects twice in development. Sharing the
                // in-flight request prevents a one-time code from being exchanged twice.
                const response = await exchangeCenterViewCodeOnce(code);
                if (cancelled) return;

                const payload = response?.data?.data ?? response?.data ?? {};
                const accessToken =
                    payload?.tokens?.access?.token ?? payload?.accessToken ?? payload?.token;
                const user = payload?.user ?? payload?.viewer ?? {};
                const center = payload?.center ?? payload?.centerContext ?? {};
                const subscriptionData = payload?.subscriptionData ?? payload?.subscription ?? {};
                const viewSession = payload?.viewSession ?? payload?.session ?? {};

                if (!accessToken || !(center?.id ?? center?.centerId ?? payload?.authObject?.centerId)) {
                    throw new Error('센터 열람 세션 응답이 올바르지 않습니다.');
                }

                const authObject = payload?.authObject ?? {
                    adminId: user?.adminId ?? payload?.viewerAdminId,
                    id: user?.id,
                    name: user?.name ?? '슈퍼관리자',
                    email: user?.email,
                    phoneNumber: user?.phoneNumber,
                    profileImageUrl: user?.profileImageUrl,
                    centerAddress: center?.address ?? center?.centerAddress,
                    centerCategory: center?.category ?? center?.centerCategory,
                    centerCode: center?.code ?? center?.centerCode,
                    centerName: center?.name ?? center?.centerName,
                    centerId: center?.id ?? center?.centerId,
                    settlementType: center?.settlementType,
                    vatPolicy: center?.vatPolicy ?? 'inclusive',
                    vatRate: center?.vatRate ?? 10,
                    coachCount: subscriptionData?.coachCount,
                    memberCount: subscriptionData?.memberCount,
                    activationDate: subscriptionData?.activationDate,
                    expirationDate: subscriptionData?.expirationDate,
                    expirationDelayDate: subscriptionData?.expirationDelayDate,
                    usableStatus: subscriptionData?.usableStatus ?? 'plan',
                    planLimitCount: subscriptionData?.planLimitCount,
                    confirmUsableStatus: true,
                };

                setCenterViewSession({
                    authorization: accessToken,
                    authObject: {
                        ...authObject,
                        role: 'center_viewer',
                        accessMode: 'view',
                        superAdminId: payload?.superAdminId ?? user?.superAdminId,
                        viewSessionId: viewSession?.id ?? payload?.viewSessionId,
                        centerName: authObject?.centerName ?? center?.name ?? center?.centerName,
                        centerId: authObject?.centerId ?? center?.id ?? center?.centerId,
                    },
                    expiresAt: normalizeExpiresAt(
                        viewSession?.expiresAt ?? payload?.expiresAt ?? payload?.tokens?.access?.expires,
                    ),
                });

                window.history.replaceState(null, '', '/center-view');
                window.location.replace('/dash');
            } catch (exchangeError: any) {
                if (cancelled) return;
                setError(
                    exchangeError?.response?.data?.message ??
                        exchangeError?.message ??
                        '센터 열람 세션을 시작할 수 없습니다.',
                );
            }
        };

        exchange();
        return () => {
            cancelled = true;
        };
    }, [code]);

    const returnToSuperAdmin = () => {
        clearCenterViewSession(true);
        window.location.replace('/superadmin');
    };

    return (
        <div style={{ minHeight: '100vh', width: '100%', display: 'grid', placeItems: 'center', background: '#F4F5F7' }}>
            <div style={{ width: 420, padding: 32, borderRadius: 16, background: '#fff', textAlign: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1A305D', marginBottom: 12 }}>
                    {error ? '센터 열람을 시작하지 못했습니다' : '센터 관리자 화면을 준비하고 있습니다'}
                </div>
                <div style={{ fontSize: 14, color: '#5E7093', lineHeight: 1.6 }}>
                    {error || '잠시만 기다려주세요.'}
                </div>
                {error && (
                    <button onClick={returnToSuperAdmin} style={{ marginTop: 20, padding: '10px 18px', border: 0, borderRadius: 8, background: '#1A305D', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                        슈퍼관리자로 돌아가기
                    </button>
                )}
            </div>
        </div>
    );
};

export default CenterViewBootstrap;
